import { logError } from '@/lib/sentry.lib';

import { useEffect, useState } from 'react';

import { selectAssetRetrievalCustomerId } from '@repo/api-adapters/selectors/asset-retrieval.selectors';
import { selectCountryCode } from '@repo/api-adapters/selectors/customer-assets.selectors';
import { selectBillingContact, selectCustomerIdentifier } from '@repo/api-adapters/selectors/customer.selectors';
import type { AssetDataType } from '@repo/types/asset';
import type { CustomerDetails, CustomerDetailsRaw } from '@repo/utils';
import { searchCustomer } from '@repo/utils';

import { getCustomerType, isRegisteredCustomer, isRimsCustomer } from '@/utils/customer-details.utils';

import { useAssetRetrieval } from './asset-retrieval.hooks';
import { useAssetParams } from './asset.hooks';

export function useCustomerDetails(asset: AssetDataType): CustomerDetails {
    const countryCode = selectCountryCode(asset);
    const { assetId, assetType } = useAssetParams();
    const customerType = getCustomerType(asset);
    const assetCustomerId = getCustomerIdentifier(asset);
    const customerIdFromAsset = assetCustomerId && assetCustomerId.length > 0 ? assetCustomerId : undefined;
    const { data: retrievedAsset } = useAssetRetrieval(assetType, assetId, !customerIdFromAsset);
    const retrievedCustomerId =
        customerType === 'Business' && retrievedAsset ? selectAssetRetrievalCustomerId(retrievedAsset) : undefined;
    const customerId = customerIdFromAsset ?? retrievedCustomerId;
    const [customerDetails, setCustomerDetails] = useState<CustomerDetailsRaw>();
    const preferredLocale = customerDetails?.preferredLocale;
    const isRegistered = isRegisteredCustomer(customerDetails);
    const isRims = customerType === 'Business' && isRimsCustomer(customerDetails);

    useEffect(() => {
        const updateCustomerDetails = async () => {
            try {
                const customerDetails = await searchCustomer(customerType, customerId, countryCode);

                setCustomerDetails(customerDetails);
            } catch (error) {
                logError(error);
            }
        };

        void updateCustomerDetails();
    }, [countryCode, customerId, customerType]);

    return {
        isRegisteredCustomer: isRegistered,
        isRimsCustomer: isRims,
        preferredLocale,
    };
}

const getCustomerIdentifier = (asset: AssetDataType) => {
    const customerId = selectCustomerIdentifier(asset);

    if (customerId) {
        return customerId;
    }

    const billingContact = selectBillingContact(asset);

    return billingContact?.partyId;
};
